import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Search, Stethoscope, Clock, ShieldCheck } from 'lucide-react';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import { API, ITEMS_PER_PAGE, paginate, getTotalPages, toggleSelectItem, toggleSelectAll, errDetail } from './adminShared';

const EMPTY_FORM = { hospital_name: '', address: '', phone: '', email: '', currency_code: 'USD' };

export default function HospitalsTab({ hospitals, currencies, reload, authCfg }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [depositDialog, setDepositDialog] = useState(false);
  const [depositHospital, setDepositHospital] = useState(null);
  const [depositAmount, setDepositAmount] = useState('');
  const [hoursHospital, setHoursHospital] = useState(null);
  const [hoursForm, setHoursForm] = useState({ enabled: false, open: '08:00', close: '20:00', manually_closed: false });

  const openHours = (hospital) => {
    const wh = hospital.working_hours || {};
    setHoursForm({
      enabled: !!wh.enabled,
      open: wh.open || '08:00',
      close: wh.close || '20:00',
      manually_closed: !!wh.manually_closed,
    });
    setHoursHospital(hospital);
  };

  const saveHours = async () => {
    try {
      await axios.post(`${API}/admin/hospitals/${encodeURIComponent(hoursHospital.hospital_name)}/working-hours`, hoursForm, authCfg);
      toast.success(`Working hours updated for ${hoursHospital.hospital_name}`);
      setHoursHospital(null);
      reload();
    } catch (error) {
      toast.error(errDetail(error, 'Failed to update working hours'));
    }
  };

  const filtered = hospitals.filter((h) =>
    h.hospital_name.toLowerCase().includes(search.toLowerCase()) ||
    (h.address || '').toLowerCase().includes(search.toLowerCase())
  );
  const paginated = paginate(filtered, page);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); };
  const openEdit = (hospital) => {
    setEditing(hospital);
    setForm({ ...hospital, currency_code: hospital.currency_code || 'USD' });
    setDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/hospitals/${editing.hospital_name}`, form, authCfg);
        toast.success('Hospital updated successfully');
      } else {
        await axios.post(`${API}/admin/hospitals`, form, authCfg);
        toast.success('Hospital created successfully');
      }
      setDialog(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      reload();
    } catch (error) {
      toast.error(errDetail(error));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (hospitalName) => {
    setConfirm({
      title: 'Delete hospital',
      description: `Delete hospital "${hospitalName}"? This will fail if the hospital has users or price lists.`,
      confirmLabel: 'Delete',
      destructive: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/hospitals/${hospitalName}`, authCfg);
          toast.success('Hospital deleted successfully');
          reload();
        } catch (error) {
          toast.error(errDetail(error, 'Failed to delete hospital'));
        }
      },
    });
  };

  const handleBulkDelete = () => {
    setConfirm({
      title: `Delete ${selected.length} hospital(s)`,
      description: 'This action cannot be undone. Deletion will fail for hospitals that still have users or price lists.',
      confirmLabel: `Delete ${selected.length}`,
      destructive: true,
      onConfirm: async () => {
        setLoading(true);
        let ok = 0, fail = 0;
        for (const name of selected) {
          try {
            await axios.delete(`${API}/admin/hospitals/${name}`, authCfg);
            ok++;
          } catch {
            fail++;
          }
        }
        toast.success(`Deleted ${ok} hospital(s)${fail > 0 ? `, ${fail} failed` : ''}`);
        setSelected([]);
        reload();
        setLoading(false);
      },
    });
  };

  const handleDeposit = async (e) => {
    e.preventDefault();
    if (!depositHospital || !depositAmount) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/hospitals/${depositHospital.hospital_name}/deposit`, { amount: parseFloat(depositAmount) }, authCfg);
      toast.success(response.data.message);
      setDepositDialog(false);
      setDepositAmount('');
      reload();
    } catch (error) {
      toast.error(errDetail(error, 'Failed to add deposit'));
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowToggle = async (hospital) => {
    try {
      const response = await axios.post(`${API}/admin/hospitals/${encodeURIComponent(hospital.hospital_name)}/workflow-toggle`, {}, authCfg);
      toast.success(response.data.message);
      reload();
    } catch (error) {
      toast.error(errDetail(error, 'Failed to toggle workflow'));
    }
  };

  const handlePreauthToggle = async (hospital) => {
    try {
      const response = await axios.post(`${API}/admin/hospitals/${encodeURIComponent(hospital.hospital_name)}/preauth-toggle`, {}, authCfg);
      toast.success(response.data.message);
      reload();
    } catch (error) {
      toast.error(errDetail(error, 'Failed to toggle pre-authorization'));
    }
  };

  return (
    <Card>
      <ConfirmDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)} {...(confirm || {})} />
      <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-3 sm:p-6">
        <CardTitle className="text-base sm:text-lg">Hospitals</CardTitle>
        <div className="flex flex-wrap gap-2">
          {selected.length > 0 && (
            <Button variant="destructive" size="sm" onClick={handleBulkDelete} className="text-xs sm:text-sm" data-testid="bulk-delete-hospitals-btn">
              <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              Delete {selected.length}
            </Button>
          )}
          <Dialog open={dialog} onOpenChange={setDialog}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={openAdd} className="text-xs sm:text-sm" data-testid="add-hospital-btn">
                <Plus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />Add
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editing ? 'Edit Hospital' : 'Add Hospital'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Hospital Name</Label>
                  <Input value={form.hospital_name} onChange={(e) => setForm({ ...form, hospital_name: e.target.value })} required disabled={!!editing} />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={form.currency_code || 'USD'} onValueChange={(value) => setForm({ ...form, currency_code: value })}>
                    <SelectTrigger><SelectValue placeholder="Select currency" /></SelectTrigger>
                    <SelectContent>
                      {currencies.map((currency) => (
                        <SelectItem key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name} ({currency.symbol})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent className="p-2 sm:p-6">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input placeholder="Search hospitals..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 text-sm" data-testid="hospital-search-input" />
          </div>
          {search && <p className="text-xs sm:text-sm text-gray-500 mt-2">Found {filtered.length} hospital(s)</p>}
        </div>
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-100">
              <tr>
                <th className="text-center p-2 sm:p-3 font-semibold w-8 sm:w-12">
                  <Checkbox
                    checked={selected.length === paginated.length && paginated.length > 0}
                    onCheckedChange={() => toggleSelectAll(paginated, selected, setSelected, 'hospital_name')}
                  />
                </th>
                <th className="text-left p-2 sm:p-3 font-semibold">Name</th>
                <th className="text-left p-2 sm:p-3 font-semibold hidden md:table-cell">Address</th>
                <th className="text-left p-2 sm:p-3 font-semibold hidden lg:table-cell">Phone</th>
                <th className="text-left p-2 sm:p-3 font-semibold hidden lg:table-cell">Email</th>
                <th className="text-center p-2 sm:p-3 font-semibold hidden sm:table-cell">Currency</th>
                <th className="text-center p-2 sm:p-3 font-semibold">
                  <span className="inline-flex items-center gap-1"><Stethoscope className="w-3 h-3" />Workflow</span>
                </th>
                <th className="text-center p-2 sm:p-3 font-semibold">
                  <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Pre-auth</span>
                </th>
                <th className="text-center p-2 sm:p-3 font-semibold">
                  <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />Hours</span>
                </th>
                <th className="text-right p-2 sm:p-3 font-semibold">Balance</th>
                <th className="text-center p-2 sm:p-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((hospital) => {
                const balance = hospital.deposit_balance || 0;
                return (
                  <tr key={hospital.hospital_name} className="border-b hover:bg-gray-50">
                    <td className="p-2 sm:p-3 text-center">
                      <Checkbox
                        checked={selected.includes(hospital.hospital_name)}
                        onCheckedChange={() => toggleSelectItem(hospital.hospital_name, selected, setSelected)}
                      />
                    </td>
                    <td className="p-2 sm:p-3 font-medium">{hospital.hospital_name}</td>
                    <td className="p-2 sm:p-3 hidden md:table-cell">{hospital.address}</td>
                    <td className="p-2 sm:p-3 hidden lg:table-cell">{hospital.phone}</td>
                    <td className="p-2 sm:p-3 hidden lg:table-cell">{hospital.email}</td>
                    <td className={`p-2 sm:p-3 text-center hidden sm:table-cell`}>
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                        {hospital.currency_code || 'USD'}
                      </span>
                    </td>
                    <td className="p-2 sm:p-3 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <button
                          onClick={() => handleWorkflowToggle(hospital)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${hospital.workflow_enabled ? 'bg-teal-600' : 'bg-gray-300'}`}
                          title={hospital.workflow_enabled ? 'Patient workflow ON — click to disable (hospital returns to direct claim creation)' : 'Patient workflow OFF — click to enable the clinical workflow for this hospital'}
                          data-testid={`workflow-toggle-${hospital.hospital_name}`}
                        >
                          <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${hospital.workflow_enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-[10px] font-medium ${hospital.workflow_enabled ? 'text-teal-600' : 'text-gray-400'}`}>
                          {hospital.workflow_enabled ? 'ON' : 'OFF'}
                        </span>
                      </div>
                    </td>
                    <td className="p-2 sm:p-3 text-center">
                      <div className="flex flex-col items-center gap-0.5">
                        <button
                          onClick={() => handlePreauthToggle(hospital)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${hospital.preauth_required ? 'bg-amber-500' : 'bg-gray-300'}`}
                          title={hospital.preauth_required ? 'Pre-authorization REQUIRED — every check-in needs superadmin approval before treatment' : 'Pre-authorization not required — click to require superadmin approval before treatment'}
                          data-testid={`preauth-toggle-${hospital.hospital_name}`}
                        >
                          <span className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${hospital.preauth_required ? 'translate-x-5' : 'translate-x-1'}`} />
                        </button>
                        <span className={`text-[10px] font-medium ${hospital.preauth_required ? 'text-amber-600' : 'text-gray-400'}`}>
                          {hospital.preauth_required ? 'ON' : 'OFF'}
                        </span>
                      </div>
                    </td>
                    <td className="p-2 sm:p-3 text-center">
                      <button
                        onClick={() => openHours(hospital)}
                        className="inline-flex flex-col items-center gap-0.5 px-2 py-1 rounded-md hover:bg-green-50 transition-colors"
                        title="Configure working hours"
                        data-testid={`hours-btn-${hospital.hospital_name}`}
                      >
                        {hospital.working_hours?.manually_closed ? (
                          <span className="text-xs font-semibold text-red-600">Closed</span>
                        ) : hospital.working_hours?.enabled ? (
                          <span className="text-xs font-semibold text-green-700">{hospital.working_hours.open}–{hospital.working_hours.close}</span>
                        ) : (
                          <span className="text-xs text-gray-400">24/7</span>
                        )}
                        <span className="text-[10px] text-blue-600 underline">edit</span>
                      </button>
                    </td>
                    <td className={`p-2 sm:p-3 text-right font-semibold ${balance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${balance.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </td>
                    <td className="p-2 sm:p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setDepositHospital(hospital); setDepositAmount(''); setDepositDialog(true); }} title="Add Deposit" data-testid={`deposit-hospital-${hospital.hospital_name}`}>
                          <Plus className="w-3 h-3 sm:w-4 sm:h-4 text-green-600" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(hospital)}>
                          <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600" onClick={() => handleDelete(hospital.hospital_name)}>
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length > ITEMS_PER_PAGE && (
          <Pagination currentPage={page} totalPages={getTotalPages(filtered.length)} onPageChange={setPage} />
        )}
      </CardContent>

      <Dialog open={depositDialog} onOpenChange={setDepositDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Deposit to {depositHospital?.hospital_name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleDeposit} className="space-y-4">
            <div>
              <Label>Current Balance</Label>
              <div className={`text-2xl font-bold ${(depositHospital?.deposit_balance || 0) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${(depositHospital?.deposit_balance || 0).toFixed(2)}
              </div>
            </div>
            <div>
              <Label>Deposit Amount ($)</Label>
              <Input type="number" step="0.01" min="0.01" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} placeholder="Enter amount" required data-testid="deposit-amount-input" />
            </div>
            {depositAmount && (
              <div className="bg-blue-50 p-3 rounded">
                <p className="text-sm text-gray-700">
                  New Balance: <span className="font-bold text-green-600">
                    ${((depositHospital?.deposit_balance || 0) + parseFloat(depositAmount || 0)).toFixed(2)}
                  </span>
                </p>
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full" data-testid="deposit-submit-btn">
              {loading ? 'Processing...' : 'Add Deposit'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!hoursHospital} onOpenChange={(o) => !o && setHoursHospital(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-600" />
              Working Hours — {hoursHospital?.hospital_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-800">Enforce working hours</p>
                <p className="text-xs text-gray-500">Outside these hours, hospital staff cannot log in or submit claims</p>
              </div>
              <button
                type="button"
                onClick={() => setHoursForm({ ...hoursForm, enabled: !hoursForm.enabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hoursForm.enabled ? 'bg-green-600' : 'bg-gray-300'}`}
                data-testid="hours-enforce-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hoursForm.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Opens at (CAT)</Label>
                <Input type="time" value={hoursForm.open} onChange={(e) => setHoursForm({ ...hoursForm, open: e.target.value })} disabled={!hoursForm.enabled} data-testid="hours-open-input" />
              </div>
              <div>
                <Label>Closes at (CAT)</Label>
                <Input type="time" value={hoursForm.close} onChange={(e) => setHoursForm({ ...hoursForm, close: e.target.value })} disabled={!hoursForm.enabled} data-testid="hours-close-input" />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
              <div>
                <p className="font-medium text-gray-800">Manually closed now</p>
                <p className="text-xs text-gray-500">Immediately blocks logins & claims regardless of schedule</p>
              </div>
              <button
                type="button"
                onClick={() => setHoursForm({ ...hoursForm, manually_closed: !hoursForm.manually_closed })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${hoursForm.manually_closed ? 'bg-red-600' : 'bg-gray-300'}`}
                data-testid="hours-manual-close-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${hoursForm.manually_closed ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            <Button onClick={saveHours} className="w-full bg-green-600 hover:bg-green-700" data-testid="hours-save-btn">
              Save Working Hours
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
